// src/App.jsx
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import './styles/global.css';
import {
  BrowserRouter, Routes, Route, NavLink,
  Navigate, useLocation,
} from 'react-router-dom';
import { auth, db } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { Nav, SloganStrip } from './components/Nav';
import ContactForm from './components/ContactForm';
import WorkflowsTab from './pages/WorkflowsTab';
import NodesTab from './pages/NodesTab';
import CloudTab from './pages/CloudTab';
import RunpodActivityPage from './pages/RunpodActivityPage';
import AdminPanel from './pages/AdminPanel';
import AdminLogin from './pages/AdminLogin';

const FOOTER_PAGES = [
  { id: 'privacy',  label: 'Privacy Policy' },
  { id: 'about',    label: 'About' },
  { id: 'contact',  label: 'Contact' },
  { id: 'terms',    label: 'Terms of Use' },
];

// ── Footer modal ──────────────────────────────────────────────────────────────
function FooterModal({ page, content, contactEmail, onClose }) {
  const isContact = page.id === 'contact';
  const isHTML    = typeof content === 'string' && content.trim().startsWith('<');

  useEffect(() => {
    const saved = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = saved;
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return ReactDOM.createPortal(
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(5,10,20,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 16px' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'var(--sand-surface)', border: '0.5px solid var(--sand-border)', borderRadius: 'var(--radius-lg)', width: '100%', maxWidth: isContact ? 520 : 620, maxHeight: '88vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '0.5px solid var(--sand-border)', flexShrink: 0 }}>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, fontWeight: 600, color: 'var(--sand-text)' }}>{page.label}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--sand-hint)', fontSize: 22, lineHeight: 1, padding: '2px 6px' }}
            onMouseEnter={e => e.currentTarget.style.color = 'var(--sand-text)'}
            onMouseLeave={e => e.currentTarget.style.color = 'var(--sand-hint)'}
          >×</button>
        </div>

        <div style={{ padding: '22px 24px', overflowY: 'auto', flex: 1 }}>
          {isContact ? (
            <ContactForm contactEmail={contactEmail} />
          ) : isHTML ? (
            <>
              <style>{`
                .footer-rich h2 { font-size:17px; font-weight:600; margin:10px 0 6px; font-family:'Playfair Display',serif; color:var(--sand-text); }
                .footer-rich h3 { font-size:14px; font-weight:600; margin:8px 0 4px; font-family:'Playfair Display',serif; color:var(--sand-text); }
                .footer-rich h4 { font-size:12px; font-weight:600; margin:6px 0 3px; font-family:'Playfair Display',serif; color:var(--sand-text); text-transform:uppercase; letter-spacing:0.5px; }
                .footer-rich p  { margin:0 0 12px; min-height:1.4em; font-size:14px; line-height:1.8; color:var(--sand-muted); font-family:'Source Serif 4',serif; }
                .footer-rich a  { color:#38bdf8; text-decoration:underline; }
                .footer-rich strong { font-weight:600; color:var(--sand-text); }
                .footer-rich em { font-style:italic; }
                .footer-rich img { max-width:100%; border-radius:6px; margin:8px 0; display:block; }
                .footer-rich iframe { width:100%; aspect-ratio:16/9; height:auto; border-radius:6px; margin:8px 0; display:block; }
              `}</style>
              <div className="footer-rich" dangerouslySetInnerHTML={{ __html: content }} />
            </>
          ) : content ? (
            <p style={{ fontSize: 14, color: 'var(--sand-muted)', lineHeight: 1.8, whiteSpace: 'pre-line', fontFamily: "'Source Serif 4', serif" }}>{content}</p>
          ) : (
            <p style={{ fontSize: 13, color: 'var(--sand-hint)', fontStyle: 'italic', fontFamily: "'Source Serif 4', serif" }}>
              No content yet — add it in the Admin panel under Parameters.
            </p>
          )}
        </div>

        {!isContact && (
          <div style={{ padding: '14px 24px', borderTop: '0.5px solid var(--sand-border)', display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
            <button onClick={onClose} style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, padding: '7px 18px', background: 'var(--sand-amber)', color: '#0e1525', border: 'none', borderRadius: 'var(--radius-sm)', cursor: 'pointer' }}>Close</button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

const TABS = [
  { path: '/workflows', label: 'Workflows', sloganKey: 'workflowsSlogan', slogan: 'If you have ComfyUI — grab a workflow and start generating.' },
  { path: '/nodes',     label: 'Nodes',     sloganKey: 'nodesSlogan',     slogan: 'Enhance ComfyUI with custom nodes.' },
  { path: '/cloud',     label: 'Cloud',     sloganKey: 'cloudSlogan',     slogan: 'Run ComfyUI remotely on powerful cloud GPUs — no local install needed.' },
];

// ── Public shell ──────────────────────────────────────────────────────────────
function PublicShell() {
  const location = useLocation();
  const tab = TABS.find(t => location.pathname.startsWith(t.path)) || TABS[0];
  const [openPage, setOpenPage]     = useState(null);
  const [parameters, setParameters] = useState({});

  useEffect(() => {
    getDoc(doc(db, 'config', 'parameters'))
      .then(snap => { if (snap.exists()) setParameters(snap.data()); })
      .catch(() => {});
  }, []);

  // Hide tab nav on the activity sub-page
  const isActivityPage = location.pathname.startsWith('/runpod-activity');

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Nav />

      {/* Hero */}
      <div style={{ padding: 'clamp(20px, 4vw, 36px) clamp(16px, 4vw, 32px) 0', textAlign: 'center' }}>
        <NavLink to="/workflows" style={{ textDecoration: 'none', display: 'inline-block' }}>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13, letterSpacing: 3, color: 'var(--sand-amber)', textTransform: 'uppercase', marginBottom: 12 }}>
            The ComfyUI resource hub
          </div>
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(22px, 5vw, 36px)', fontWeight: 700, color: 'var(--sand-text)', lineHeight: 1.2, letterSpacing: '-0.5px', margin: '0 auto', textAlign: 'center', maxWidth: '100%', wordBreak: 'break-word' }}>
            Everything you need for ComfyUI
          </h1>
        </NavLink>
        <div style={{ width: 40, height: 2, background: 'var(--sand-amber)', margin: '16px auto 0' }} />
      </div>

      {/* Tab nav — hidden on activity sub-page */}
      {!isActivityPage && (
        <div style={{ padding: '20px clamp(12px, 4vw, 32px) 0' }}>
          <div style={{ display: 'flex', borderBottom: '1.5px solid var(--sand-border)' }}>
            {TABS.map(t => (
              <NavLink key={t.path} to={t.path} style={({ isActive }) => ({
                fontFamily: "'Source Serif 4', serif", fontSize: 17,
                fontWeight: isActive ? 500 : 400, padding: '10px 22px',
                color: isActive ? 'var(--sand-amber)' : 'var(--sand-muted)',
                textDecoration: 'none',
                borderBottom: isActive ? '2px solid var(--sand-amber)' : '2px solid transparent',
                marginBottom: '-1.5px', transition: 'color 0.2s, border-color 0.2s',
                letterSpacing: '0.2px', display: 'inline-block',
              })}>{t.label}</NavLink>
            ))}
          </div>
        </div>
      )}

      {/* Slogan — hidden on activity sub-page */}
      {!isActivityPage && <SloganStrip key={tab.path} text={parameters[tab.sloganKey] || tab.slogan} />}

      {/* Content */}
      <main style={{ flex: 1, padding: 'clamp(16px, 3vw, 24px) clamp(12px, 4vw, 32px) 48px', width: '100%', alignSelf: 'stretch' }}>
        <Routes>
          <Route index element={<Navigate to="/workflows" replace />} />
          <Route path="workflows"          element={<WorkflowsTab />} />
          <Route path="workflows/:id"      element={<WorkflowsTab />} />
          <Route path="nodes"              element={<NodesTab />} />
          <Route path="cloud"              element={<CloudTab />} />
          <Route path="runpod-activity"    element={<RunpodActivityPage />} />
          {/* Legacy redirect */}
          <Route path="runpod"             element={<Navigate to="/cloud" replace />} />
          <Route path="*"                  element={<Navigate to="/workflows" replace />} />
        </Routes>
      </main>

      {/* Footer */}
      <footer style={{ borderTop: '1.5px solid var(--sand-border)', padding: '16px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 14, color: 'var(--sand-muted)' }}>
          Comfy<span style={{ color: 'var(--sand-amber)' }}>Stack</span>
        </span>
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
          {FOOTER_PAGES.map(p => (
            <span key={p.id} onClick={() => setOpenPage(p)}
              style={{ fontSize: 11, color: 'var(--sand-amber)', cursor: 'pointer', transition: 'color 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.color = '#fff'}
              onMouseLeave={e => e.currentTarget.style.color = 'var(--sand-amber)'}
            >{p.label}</span>
          ))}
        </div>
      </footer>

      {openPage && (
        <FooterModal
          page={openPage}
          content={parameters[openPage.id]}
          contactEmail={parameters.contactEmail}
          onClose={() => setOpenPage(null)}
        />
      )}
    </div>
  );
}

// ── Admin guard ───────────────────────────────────────────────────────────────
function AdminGuard() {
  const [user, setUser] = useState(undefined);
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, u => setUser(u || null));
    return unsub;
  }, []);
  if (user === undefined) return null;
  if (!user) return <AdminLogin onLogin={setUser} />;
  return <AdminPanel user={user} />;
}

// ── Root ──────────────────────────────────────────────────────────────────────
export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/admin/*" element={<AdminGuard />} />
        <Route path="/*"       element={<PublicShell />} />
      </Routes>
    </BrowserRouter>
  );
}
